import { Controller } from "@hotwired/stimulus";
import { DummyData } from "../../utils/dummy_data";

export default class extends Controller {
  static values = {
    commonKeywords: Array,
    phonePatterns: Array,
    dummyEmailUsernames: Array,
    applicantEmail: String,
    existingAddresses: Array,
    validationMessages: Object,
    noReferenceModalUrl: String,
  };

  static targets = ["addressStreet2ConfirmationSelect", "street2Input"];

  connect() {
    this.initializeElements();
    this.setupEventListeners();
    this.updateVisibility(this.landlordRelationshipSelect.value);
    this.handleResidentStatusChange(this.residentStatusSelect.value);
    this.setupAddressRecognitionToggle();
  }

  addressStreet2ConfirmationSelectTargetConnected() {
    this.addressStreet2ConfirmationSelectTarget.addEventListener("change", (e) => {
      this.toggleStreet2Required(e.target.value);
    });
  }

  initializeElements() {
    this.residentStatusSelect = this.element.querySelector("#applicant_form_resident_status");
    this.landlordRelationshipSelect = this.element.querySelector("#applicant_form_relationship_to_applicant");
    this.nonSelfFields = this.element.querySelector("#applicant_form_non_self_fields");
    this.nonSelfAdditionalFields = this.element.querySelector("#applicant_form_non_self_additional_fields");
    this.selfAdditionalFields = this.element.querySelector("#applicant_form_self_additional_fields");
    this.submitButton = this.element.querySelector('input[type="submit"]');
  }

  setupEventListeners() {
    this.residentStatusSelect.addEventListener("change", (e) => {
      this.handleResidentStatusChange(e.target.value);
    });

    this.landlordRelationshipSelect.addEventListener("change", (e) => {
      this.updateVisibility(e.target.value);
    });

    // Add event listeners for input fields to validate dummy data
    const inputFields = this.element.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]');
    inputFields.forEach((input) => {
      input.addEventListener("input", (e) => {
        this.validateDummyData(e);
      });
    });
  }

  setupAddressRecognitionToggle(toggleId = "address-recognition-toggle", checkboxId = "address-recognition-checkbox") {
    // Find elements by ID instead of class selectors
    this.addressRecognitionToggle = this.element.querySelector(`#${toggleId}`);

    this.addressRecognitionCheckbox = this.element.querySelector(`#${checkboxId}`);

    if (!this.addressRecognitionToggle || !this.addressRecognitionCheckbox) return;

    // Fix the typo in the text
    this.addressRecognitionToggle.textContent = "Don't Recognize this address?";

    // Make the text clickable
    this.addressRecognitionToggle.style.cursor = "pointer";

    // Initially hide the checkbox
    this.addressRecognitionCheckbox.hidden = true;

    // Add click event to toggle visibility
    this.addressRecognitionToggle.addEventListener("click", (e) => {
      this.addressRecognitionCheckbox.hidden = !this.addressRecognitionCheckbox.hidden;
    });
  }

  handleResidentStatusChange(value) {
    const moveOutDateWrapper = this.element.querySelector("#applicant_form_move_out_date_wrapper");
    if (!moveOutDateWrapper) return;

    const moveOutDateInput = moveOutDateWrapper.querySelector("#applicant_form_move_out_date_input");
    if (!moveOutDateInput) return;

    switch (value) {
      case "":
      case "CURRENT_RESIDENT":
        moveOutDateWrapper.hidden = true;
        moveOutDateInput.disabled = true;
        moveOutDateInput.required = false;
        break;
      default:
        moveOutDateWrapper.hidden = false;
        moveOutDateInput.disabled = false;
        moveOutDateInput.required = true;
    }
  }

  updateVisibility(value) {
    switch (value) {
      case "":
        this.toggleAllFieldsVisibility();
        break;
      case "SELF":
        this.toggleSelfFieldsVisibility();
        break;
      default:
        this.toggleNonSelfFieldsVisibility();
    }
  }

  toggleAllFieldsVisibility() {
    this.nonSelfFields.hidden = true;
    this.nonSelfAdditionalFields.hidden = true;
    this.selfAdditionalFields.hidden = true;
  }

  toggleNonSelfFieldsVisibility() {
    this.nonSelfFields.hidden = false;
    this.nonSelfAdditionalFields.hidden = false;
    this.selfAdditionalFields.hidden = true;
  }

  toggleSelfFieldsVisibility() {
    this.nonSelfFields.hidden = true;
    this.nonSelfAdditionalFields.hidden = true;
    this.selfAdditionalFields.hidden = false;
  }

  validateDummyData(event) {
    const input = event.target;
    const value = input.value;
    const inputType = input.type;
    const inputName = input.name;

    let isDummy = false;
    let message = "";

    if (inputName.includes("email")) {
      isDummy = DummyData.dummyEmail(value, new Set(this.dummyEmailUsernamesValue));
      if (isDummy) message = this.validationMessagesValue.email;

      if (DummyData.sameEmailAsApplicant(value, this.applicantEmailValue)) {
        isDummy = true;
        message = this.validationMessagesValue.same_email;
      }
    } else if (inputName.includes("phone")) {
      isDummy = DummyData.dummyPhone(value, this.phonePatternsValue);
      if (isDummy) message = this.validationMessagesValue.phone;
    } else if (inputName.includes("first_name") || inputName.includes("last_name")) {
      isDummy = DummyData.dummyResponse(value, new Set(this.commonKeywordsValue));
      if (isDummy) message = this.validationMessagesValue.name;
    } else if (inputName.includes("applicant_address_")) {
      // Handle duplicate address validation separately
      this.handleDuplicateAddressValidation();
      return;
    }

    if (isDummy) {
      this.showValidationMessage(input, message);
    } else {
      this.removeValidationMessage(input);
    }
  }

  showValidationMessage(input, message, isHtml = false) {
    // Remove any existing message
    this.removeValidationMessage(input);

    // Create and append the message
    const messageDiv = document.createElement("div");
    messageDiv.className = "text-alert mt-1 small";

    if (isHtml) {
      messageDiv.innerHTML = message;
    } else {
      messageDiv.textContent = message;
    }

    messageDiv.style.fontSize = "0.875rem";
    messageDiv.style.color = "#ff0000";
    messageDiv.style.fontWeight = "500";
    messageDiv.style.marginTop = "0.25rem";

    // Insert after the input
    input.parentNode.insertBefore(messageDiv, input.nextSibling);

    // Disable submit button
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add("opacity-50", "cursor-not-allowed");
    }
  }

  removeValidationMessage(input) {
    const messageDiv = input.parentNode.querySelector(".text-alert");
    if (messageDiv) {
      messageDiv.remove();
    }

    // Check if there are any remaining validation messages
    const remainingMessages = this.element.querySelectorAll(".text-alert");
    if (remainingMessages.length === 0 && this.submitButton) {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove("opacity-50", "cursor-not-allowed");
    }
  }

  handleDuplicateAddressValidation() {
    const street1Field = this.element.querySelector('input[name*="applicant_address_street1"]');
    if (!street1Field) return;

    // Clear any existing duplicate address messages from all address fields
    this.clearDuplicateAddressMessages();

    const isDuplicateAddress = this.validateAddressForDuplicates();

    if (isDuplicateAddress) {
      const messageTemplate = this.validationMessagesValue.duplicate_address;
      const clickHereLink = `<a href="${this.noReferenceModalUrlValue}" data-turbo-frame="modal" class="text-dark underline">click here</a>`;
      const messageWithLink = messageTemplate.replace("click here", clickHereLink);
      this.showValidationMessage(street1Field, messageWithLink, true); // Always show under street1 field
    }
  }

  clearDuplicateAddressMessages() {
    // Find all address fields and remove duplicate address validation messages
    const addressFields = this.element.querySelectorAll(
      'input[name*="applicant_address_"], select[name*="applicant_address_"]',
    );
    addressFields.forEach((field) => {
      const messageDiv = field.parentNode.querySelector(".text-alert");
      if (messageDiv && messageDiv.innerHTML.includes(this.validationMessagesValue.duplicate_address.split(".")[0])) {
        messageDiv.remove();
      }
    });

    // Re-enable submit button if no other validation messages exist
    const remainingMessages = this.element.querySelectorAll(".text-alert");
    if (remainingMessages.length === 0 && this.submitButton) {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove("opacity-50", "cursor-not-allowed");
    }
  }

  validateAddressForDuplicates() {
    // Check if we have existing addresses to compare against
    if (!this.hasExistingAddressesValue || this.existingAddressesValue.length === 0) {
      return false;
    }

    // Get current address data from all address fields
    const street1Field = this.element.querySelector('input[name*="applicant_address_street1"]');
    const street2Field = this.element.querySelector('input[name*="applicant_address_street2"]');
    const cityField = this.element.querySelector('input[name*="applicant_address_city"]');
    const stateField = this.element.querySelector('select[name*="applicant_address_state"]');
    const zipField = this.element.querySelector('input[name*="applicant_address_zip"]');

    if (!street1Field || !cityField || !stateField || !zipField) {
      return false; // Not all required fields are present
    }

    const currentAddress = {
      street1: street1Field.value || "",
      street2: street2Field ? street2Field.value : "",
      city: cityField.value || "",
      state: stateField.value || "",
      zip: zipField.value || "",
    };

    // Only validate if all required fields have values
    if (!currentAddress.street1 || !currentAddress.city || !currentAddress.state || !currentAddress.zip) {
      return false;
    }

    // Check for duplicate using DummyData utility
    return DummyData.duplicateAddress(currentAddress, this.existingAddressesValue);
  }

  toggleStreet2Required(required) {
    const label = this.street2InputTarget.parentElement.querySelector(".form-label");

    if (required === "true") {
      this.street2InputTarget.required = true;
      label.classList.add("required");
    } else {
      this.street2InputTarget.required = false;
      label.classList.remove("required");
    }
  }
}
